/**
 * Critique Content Parser
 * Feature: 069-markdown-edits-migration
 *
 * Parses critique content to extract both markup and edit blocks.
 * Used in Phase 7 to generate edits from markdown blocks instead of database.
 */

import { parseMarkupBlocks } from './markupBlockProcessor';
import { parseEditBlocks } from './editBlockProcessor';
import type { CritiqueEdit, EditData } from '@/types/edit.types';
import logger from './logger';

// Counter for generating unique IDs within a session
let idCounter = 0;

/**
 * Result of parsing critique content for all block types
 */
export interface ParsedCritiqueContent {
  /** The edit suggestion (crop/light/combined) if found */
  edit?: CritiqueEdit;
  /** Array of markup annotations if found */
  markup?: CritiqueEdit[];
  /** Cleaned markdown content with blocks removed */
  cleanedContent: string;
  /** Any parsing errors (non-blocking) */
  errors: string[];
}

/**
 * Parse critique content for both markup and edit blocks
 *
 * This function extracts:
 * - ```markup blocks → converted to markup annotations
 * - ```edit blocks → converted to crop/light edit suggestions
 *
 * @param content - Raw critique content (may contain synthesized blocks)
 * @returns Parsed edits, markup, cleaned content, and errors
 *
 * @example
 * const result = parseCritiqueContent(critique.content);
 * // Use result.edit for edit suggestion preview
 * // Use result.markup for markup annotation previews
 */
export function parseCritiqueContent(content: string): ParsedCritiqueContent {
  logger.debug('CritiqueContentParser', 'Parsing critique content', { contentLength: content.length });

  const errors: string[] = [];
  let workingContent = content;

  // Parse markup blocks first
  const markupResult = parseMarkupBlocks(workingContent);
  errors.push(...markupResult.errors);
  workingContent = markupResult.cleanedMarkdown;

  // Parse edit blocks from remaining content
  const editResult = parseEditBlocks(workingContent);
  errors.push(...editResult.errors);
  workingContent = editResult.cleanedMarkdown;

  // Convert markup shapes to CritiqueEdit format
  const markup: CritiqueEdit[] = [];
  if (markupResult.shapes.length > 0) {
    // Group all shapes into a single markup edit (matching existing behavior)
    // Use type assertion since markup editData has different shape than crop/light
    markup.push({
      id: `markup-${Date.now()}-${++idCounter}`,
      critiqueId: '',
      editType: 'markup',
      editData: { shapes: markupResult.shapes } as unknown as EditData,
      baseEditId: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  }

  // Convert edit data to CritiqueEdit format
  let edit: CritiqueEdit | undefined;
  if (editResult.edits.length > 0) {
    // Take the first edit block (we only support one edit suggestion per critique)
    const parsedEdit = editResult.edits[0];
    if (parsedEdit.type === 'edit') {
      const data = parsedEdit.data as { crop?: any; light?: any };

      // Determine edit type and extract appropriate data
      // EditPreviewRenderer expects editData to be unwrapped:
      // - 'crop': { x, y, width, height, aspectRatio }
      // - 'light': { exposure, contrast, ... }
      // - 'combined': { crop: {...}, light: {...} }
      let editType: 'crop' | 'light' | 'combined' = 'combined';
      let editData: any = data;

      if (data.crop && !data.light) {
        editType = 'crop';
        editData = data.crop;  // Unwrap crop data
      } else if (data.light && !data.crop) {
        editType = 'light';
        editData = data.light;  // Unwrap light data
      }
      // For 'combined', keep data as { crop: {...}, light: {...} }

      edit = {
        id: `edit-${Date.now()}-${++idCounter}`,
        critiqueId: '',
        editType,
        editData,
        baseEditId: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    }

    // Log warning if multiple edit blocks found
    if (editResult.edits.length > 1) {
      logger.warn('CritiqueContentParser', 'Multiple edit blocks found, using first one', {
        count: editResult.edits.length,
      });
    }
  }

  logger.info('CritiqueContentParser', 'Parsed critique content', {
    hasEdit: !!edit,
    markupCount: markup.length,
    errorCount: errors.length,
    originalLength: content.length,
    cleanedLength: workingContent.length,
  });

  return {
    edit,
    markup: markup.length > 0 ? markup : undefined,
    cleanedContent: workingContent,
    errors,
  };
}

/**
 * Check if content has any edit or markup blocks
 * Quick check without full parsing
 */
export function hasEditBlocks(content: string): boolean {
  return content.includes('```edit') || content.includes('```markup');
}
